/**
 * TrialBanner — a slim notice at the top of every operator page while the
 * org is on a trial or has a failed payment. Renders nothing while billing
 * is off, and nothing on the Billing page itself (it has its own notices).
 */
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Callout, Link } from '@radix-ui/themes';
import { useBilling } from '../../utils/billing';

const TrialBanner: React.FC = () => {
  const { billing } = useBilling();
  const { pathname } = useLocation();
  if (!billing || pathname === '/billing') return null;

  const { status, trialDaysLeft, name } = billing.plan;
  let text: string | null = null;
  let color: 'blue' | 'red' = 'blue';
  if (status === 'suspended') {
    // Suspension applies with billing on or off.
    text = 'This organization is suspended. Running game nights still work, but you cannot create anything new.';
    color = 'red';
  } else if (!billing.billingEnabled) {
    return null;
  } else if (status === 'trialing') {
    text = `${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} left in your ${name} trial.`;
  } else if (status === 'past_due') {
    text = 'Your last payment failed. Your game nights keep working. Update your card to keep your plan.';
    color = 'red';
  }
  if (!text) return null;

  return (
    <Box mb="3">
      <Callout.Root size="1" color={color}>
        <Callout.Text>
          {text}{' '}
          {billing.canManage && status !== 'suspended' && (
            <Link asChild weight="medium">
              <RouterLink to="/billing">{status === 'trialing' ? 'Choose a plan' : 'Fix billing'}</RouterLink>
            </Link>
          )}
        </Callout.Text>
      </Callout.Root>
    </Box>
  );
};

export default TrialBanner;
